import React from 'react';

/**
 * Root-level React Error Boundary.
 *
 * Wraps the entire app so that any uncaught render error — including those
 * that can occur when the Capacitor WebView renderer is restarted after a
 * background kill and a component's state gets into an inconsistent shape —
 * shows a user-friendly reload prompt instead of a permanent blank screen.
 *
 * On a physical Android device this manifests as a black screen the user
 * has to force-quit to escape. This boundary gives them a one-tap escape
 * hatch without needing to kill the app.
 */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log to console for debugging — harmless in production.
    console.error('[Allure] Caught by root ErrorBoundary:', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const dark = document.documentElement.classList.contains('dark');
    const bg = dark ? '#121217' : '#F8F8F8';
    const text = dark ? '#E8E8F0' : '#1A1A2E';
    const sub = dark ? '#9090A8' : '#666680';
    const accent = '#703FF6';

    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: bg,
          padding: '32px 24px',
          fontFamily: "'Manrope', system-ui, sans-serif",
          textAlign: 'center',
        }}
      >
        <img
          src="/allure-logo.png"
          alt="Allure"
          style={{ width: 64, height: 64, objectFit: 'contain', marginBottom: 24, opacity: 0.85 }}
        />
        <p style={{ fontSize: 17, fontWeight: 700, color: text, margin: '0 0 8px' }}>
          Something went wrong
        </p>
        <p style={{ fontSize: 13, color: sub, margin: '0 0 32px', maxWidth: 280, lineHeight: 1.5 }}>
          The app encountered an unexpected error. Tap below to reload.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: accent,
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            padding: '13px 32px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.02em',
          }}
        >
          Reload app
        </button>
      </div>
    );
  }
}
